/**
 * Step bonus — "hit the mark, earn the money" for a Build Your Own card.
 *
 * The original bonus on a custom card is a commission: a percentage of
 * whatever the metric reaches. That is the right rule for a dollar TOTAL and
 * nonsense for anything else — 10% of $182/hr is $18.20, 10% of a 70% Beat the
 * Yeti rate is seven cents. A rate, a per-hour figure or a team standing is
 * paid the way people actually run those bonuses: clear a line, earn a set
 * amount; clear the next line, earn more.
 *
 *   { type: "steps", label, ofMetric, per: "player" | "team",
 *     steps: [{ at: 60, pay: 100 }, { at: 75, pay: 250 }, …] }
 *
 * `at` is in the metric's OWN reading units, the way an admin says it out loud:
 *   %  metric → a whole percent (70 = 70%; rates are stored as 0–1 fractions
 *               and lifted here, the same convention payout-curve gates use)
 *   $  metric → dollars ($/hr for Technicians' Revenue per Hour)
 *   #  metric → the count itself
 *
 * A commission bonus keeps its old shape ({ label, pct, ofMetric }, no `type`),
 * so every card saved before steps existed reads exactly as it did.
 *
 * Everything here is pure — the editor's preview, the card and the layout
 * store all go through the same sanitizer, and the server mirrors it
 * (gmb-server/src/controllers/workspaceLayout.js).
 */

export const MAX_BONUS_STEPS = 4;
export const BONUS_PER = ["player", "team"];
export const MAX_PER_LABEL = 24;

const num = (v) => {
  if (v === "" || v == null) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};
const round2 = (n) => Math.round(n * 100) / 100;

export const isStepBonus = (b) => !!(b && b.type === "steps");

/* What the card shows around the bonus. Everything is on by default, so only
   the OFF state is stored (`hidePill: true`) and every card saved before these
   switches existed keeps showing all of it. The step boxes and the next-step
   line only exist on a step bonus. */
export const BONUS_DISPLAY_KEYS = ["hidePill", "hideSteps", "hideNext"];
/* What the header pill says on a step bonus: "Up to $350" (the top step, the
   default) or "Bonus Opportunity $2,450" (the top step times every player it
   pays). Only the non-default is stored. */
export const PILL_OPPORTUNITY = "opportunity";
const displayFlags = (b, steps) => {
  const out = {};
  /* What the step boxes say under each amount ("Per Tech"). Free text, so it is
     trimmed and capped; empty is not stored and falls back to "Per player". */
  if (steps && typeof b.perLabel === "string" && b.perLabel.trim()) out.perLabel = b.perLabel.trim().slice(0, MAX_PER_LABEL);
  if (b.hidePill === true) out.hidePill = true;
  if (steps && b.pill === PILL_OPPORTUNITY) out.pill = PILL_OPPORTUNITY;
  if (steps && b.hideSteps === true) out.hideSteps = true;
  if (steps && b.hideNext === true) out.hideNext = true;
  return out;
};

/**
 * Whether a commission can honestly price this metric. Only a running total
 * can: a rate, a per-hour figure or an average is not an amount of money that
 * was brought in, so a percentage of it is not money anyone earned. Derived
 * metrics (Average Daily Revenue, Technicians' Revenue per Hour, Beat the Yeti
 * rate) are all ratios or standings.
 *
 * Takes a metric-catalog entry ({ kind, derived }) or a live tile
 * ({ kind, _derived }) — both shapes reach here.
 */
export const commissionFits = (metric) => {
  if (!metric) return true;
  if (metric.derived || metric._derived) return false;
  return metric.kind !== "%";
};

/** Persist shape — drops junk, sorts the ladder, and returns null when there is
    nothing payable, so an unfinished bonus never reaches the board. */
export const sanitizeBonus = (b) => {
  if (!b || typeof b !== "object") return null;
  if (typeof b.ofMetric !== "string" || !b.ofMetric) return null;
  const label = typeof b.label === "string" && b.label.trim() ? b.label.trim().slice(0, 60) : "Earned";
  if (b.type === "steps") {
    const steps = (Array.isArray(b.steps) ? b.steps : [])
      .map((s) => ({ at: num(s && s.at), pay: num(s && s.pay) }))
      .filter((s) => s.at > 0 && s.pay > 0)
      .map((s) => ({ at: round2(s.at), pay: round2(s.pay) }))
      .sort((a, c) => a.at - c.at)
      /* Two steps on the same line cannot both be "the next one" — keep the
         first as typed. */
      .filter((s, i, arr) => i === 0 || s.at !== arr[i - 1].at)
      .slice(0, MAX_BONUS_STEPS);
    if (!steps.length) return null;
    return {
      type: "steps",
      label,
      ofMetric: b.ofMetric,
      per: b.per === "team" ? "team" : "player",
      steps,
      ...displayFlags(b, true),
    };
  }
  const pct = num(b.pct);
  if (!(pct > 0)) return null;
  return { label, pct: Math.min(100, pct), ofMetric: b.ofMetric, ...displayFlags(b, false) };
};

/** The metric's live value in the units its steps are written in. */
export const stepValueOf = (amount, kind) => {
  const v = Number(amount) || 0;
  /* 0.29 × 100 is 28.999…; without the round a team sitting exactly on a 29%
     line would read as one hair short of it. */
  if (kind === "%") return round2(Math.abs(v) <= 1 ? v * 100 : v);
  return round2(v);
};

/**
 * Where the value stands on the ladder.
 *
 *   idx    the highest step reached (-1 = none yet)
 *   pay    what that step pays (0 below the first line)
 *   next   the next step up, or null at the top
 *   gap    how far below `next` the value is, in step units
 *   max    the top step's pay — "up to $X"
 */
export const stepBonusState = (steps, value) => {
  const list = Array.isArray(steps) ? steps : [];
  const v = Number(value) || 0;
  let idx = -1;
  list.forEach((s, i) => { if (v >= s.at) idx = i; });
  const next = list[idx + 1] || null;
  return {
    idx,
    pay: idx >= 0 ? list[idx].pay : 0,
    next,
    gap: next ? round2(next.at - v) : 0,
    max: list.length ? Math.max(...list.map((s) => s.pay)) : 0,
    done: list.length > 0 && idx === list.length - 1,
  };
};

/**
 * Beat the Yeti rate is a head-count in disguise: "75% of the team ahead of the
 * Yeti" on a ten-player roster means eight players, so the useful thing to say
 * is how many more people have to pass it, not how many points the rate is
 * short. Same rounding the rate itself reads with (stepValueOf).
 */
export const playersToGo = (atPct, beating, players) => {
  const p = Number(players) || 0;
  if (p <= 0) return null;
  const need = Math.ceil(round2((Number(atPct) / 100) * p) - 1e-9);
  return Math.max(0, need - (Number(beating) || 0));
};

/** The small caption under each step's amount, saying the amount is one
    person's and not the total. A team pot is one figure and needs none unless
    somebody wrote one. */
export const perCaptionOf = (bonus) => {
  if (!bonus) return "";
  if (bonus.perLabel) return bonus.perLabel;
  return bonus.per === "team" ? "" : "Per player";
};

/**
 * Horizontal placement (0–100) for the value and every step on the progress
 * rail. Every rail starts at zero, so the fill and the marks are in true
 * proportion — "$176 of $200" lands at 88% of the way to the $200 mark, not
 * wherever a rail that began at $150 would put it. The track runs a little past
 * the top step so that step is a real mark with room to beat it.
 */
export const stepRail = (steps, value, kind) => {
  const list = Array.isArray(steps) ? steps : [];
  if (!list.length) return { valueX: 0, marks: [] };
  const first = list[0].at;
  const top = list[list.length - 1].at;
  const span = top - first;
  const hi = kind === "%" ? Math.max(100, top) : top + Math.max(span * 0.12, top * 0.06);
  const xOf = (v) => {
    const x = (Number(v) / (hi || 1)) * 100;
    return Math.max(0, Math.min(100, x));
  };
  return { valueX: xOf(value), marks: list.map((s) => xOf(s.at)) };
};

/** A ladder to start from, so switching steps on shows a real example rather
    than an empty form. Built off the metric's goal when it has one. */
export const defaultStepsFor = (metric) => {
  const kind = metric && metric.kind;
  if (kind === "%") {
    return [{ at: 50, pay: 100 }, { at: 70, pay: 250 }, { at: 90, pay: 500 }];
  }
  const goal = Number(metric && metric.target) || 0;
  if (goal > 0) {
    const r = goal >= 1000 ? 100 : 5;
    const at = (f) => Math.max(1, Math.round((goal * f) / r) * r);
    return [{ at: at(0.9), pay: 100 }, { at: at(1), pay: 250 }, { at: at(1.15), pay: 500 }];
  }
  if (/per hour/i.test(`${(metric && (metric.label || metric.title)) || ""}`)) {
    return [{ at: 150, pay: 100 }, { at: 175, pay: 250 }, { at: 200, pay: 500 }];
  }
  return [{ at: "", pay: "" }];
};

/** "/hr" for a per-hour metric — the only unit a bare "$175" leaves ambiguous. */
export const rateSuffixOf = (metric) =>
  (/per hour/i.test(`${(metric && (metric.title || metric.name || metric.label)) || ""}`) ? "/hr" : "");

const trimNum = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/** A step line in the metric's words: "70%", "$175/hr", "12". */
export const stepAtLabel = (at, kind, suffix = "") => {
  if (kind === "%") return `${trimNum(at)}%`;
  if (kind === "$") return `$${trimNum(at)}${suffix}`;
  return trimNum(at);
};

export const moneyLabel = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

/**
 * The header pill's words for a step bonus. "Up to" is the top step alone, which
 * undersells a per-player bonus: $350 each across 7 players is $2,450 on the
 * table. `players` is how many the bonus pays; a team pot is one payment however
 * many people share it, and an unknown head-count falls back to the top step
 * rather than inventing a multiplier.
 */
export const bonusPillText = (bonus, players) => {
  const top = bonus.steps.length ? Math.max(...bonus.steps.map((s) => s.pay)) : 0;
  if (bonus.pill !== PILL_OPPORTUNITY) return `Up to ${moneyLabel(top)}`;
  const heads = bonus.per === "team" ? 1 : Math.max(1, Math.floor(Number(players) || 0));
  return `Bonus Opportunity ${moneyLabel(top * heads)}`;
};
